import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EventTypeEnum, MatchUpTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { positionActionConstants } from 'tods-competition-factory';

import { eventAtomFamily } from 'atoms/events';
import StandardButton from 'components/buttons/StandardButton';
import TMXDialog, { type TMXDialogProps } from 'components/dialogs/TMXDialog';
import { getPlayerNameFromMatchUp } from 'components/dialogs/utils';
import RadioButtonGroup from 'components/radioButtonGroup/RadioButtonGroup';
import AssignParticipantsTable from 'components/tables/assignParticipantsTable/AssignParticipantsTable';
import { BodyLarge, FormLabelTextLarge, H3 } from 'components/typography/Typography';
import { usePromoteToMainDrawDialog } from 'containers/dialogs/PromoteToMainDraw';
import { useTournamentService } from 'hooks/tournament/useTournamentService';
import { type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { type ParticipantInterface, type SeedAssignment } from 'typedefs/store/tmxTypes';

import {
  getMatchUptSideFromDrawPosition,
  getPayloadParticipants,
  getSelectDoublesPlayerRadioOptions,
  type PayloadParticipant,
} from './utils';

const { MODIFY_PAIR_ASSIGNMENT } = positionActionConstants;

export interface AssignParticipantDialogProps extends TMXDialogProps {
  open: boolean;
  payload: any;
  matchUp: MatchUpInterface;
  drawPosition?: number;
  seedAssignements?: SeedAssignment[];
  loading: boolean;
  onExited?: () => void;
  onSave: (payload: any) => Promise<void>;
}

const AssignParticipantDialog: React.FC<React.PropsWithChildren<AssignParticipantDialogProps>> = ({
  loading,
  payload,
  matchUp,
  drawPosition,
  seedAssignements,
  onExited,
  onSave,
  ...props
}) => {
  const { t } = useTranslation();
  const { tournamentId, eventId } = useParams<{ tournamentId: string; eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));

  const isTeam = matchUp?.matchUpType === MatchUpTypeEnum.TEAM;
  const isDoubles = matchUp?.matchUpType === MatchUpTypeEnum.DOUBLES || event?.eventType === EventTypeEnum.DOUBLES;
  const isModifyDoublesPair = payload?.[0]?.type === MODIFY_PAIR_ASSIGNMENT;
  const isAlternate = payload?.[0]?.isAlternate;
  const tournamentService = useTournamentService();
  const openPromoteToMainDrawDialog = usePromoteToMainDrawDialog();
  const payloadParticipants = useMemo(() => getPayloadParticipants(payload), [payload]);

  const initialSelectedParticipantPayload = useMemo(
    () => ({
      ...payloadParticipants[0],
      existingIndividualParticipant: payload?.[0]?.existingIndividualParticipants?.[0] || null,
    }),
    [payloadParticipants, payload],
  );
  const side = useMemo(
    () => getMatchUptSideFromDrawPosition(matchUp?.sides, drawPosition),
    [drawPosition, matchUp?.sides],
  );
  const currentParticipant = getPlayerNameFromMatchUp(matchUp, drawPosition);
  const [eventCostLoading, setEventCostLoading] = useState(false);
  const [selectedParticipantPayload, setSelectedParticipantPayload] = useState<PayloadParticipant>(
    initialSelectedParticipantPayload,
  );

  const handleClose = useCallback(
    (e) => {
      if (typeof props.onClose === 'function') {
        props.onClose(e, 'escapeKeyDown');
        setSelectedParticipantPayload(null);
      }
    },
    [props],
  );

  const handleSave = useCallback(
    async (e) => {
      const saveAndCloseAction = async () => {
        await onSave(selectedParticipantPayload);
        handleClose(e);
      };

      if (isModifyDoublesPair && !isAlternate) {
        const selectedParticipantId = selectedParticipantPayload?.participant?.participantId;
        setEventCostLoading(true);
        const eventCostResult = await tournamentService.getEventsCosts(
          [payload?.[0]?.payload?.eventId || matchUp?.eventId],
          (selectedParticipantId && [selectedParticipantId]) || [],
          tournamentId,
          'network-only',
        );
        setEventCostLoading(false);
        eventCostResult.data?.length
          ? openPromoteToMainDrawDialog({
              eventsIds: [payload?.[0]?.payload?.eventId],
              promoteParticipants: [selectedParticipantPayload?.participant],
              tournamentId: tournamentId,
              loading,
              participantEventCostData: eventCostResult.data,
              onPromotePlayers: saveAndCloseAction,
            })
          : saveAndCloseAction();
      } else {
        saveAndCloseAction();
      }
    },
    [
      isModifyDoublesPair,
      isAlternate,
      onSave,
      selectedParticipantPayload,
      handleClose,
      tournamentService,
      payload,
      matchUp?.eventId,
      tournamentId,
      openPromoteToMainDrawDialog,
      loading,
    ],
  );

  const handleChange = useCallback(
    (participantId: string) => {
      const participantPayload = payloadParticipants.find((p) => p.participant.participantId === participantId);
      setSelectedParticipantPayload({
        ...selectedParticipantPayload,
        ...participantPayload,
        participant: participantPayload?.participant,
      });
    },
    [payloadParticipants, selectedParticipantPayload],
  );

  const selectDoublesPlayerRadioOptions = useMemo(
    () => getSelectDoublesPlayerRadioOptions(payload?.[0]?.existingIndividualParticipants),
    [payload],
  );

  const handleSelectPlayerRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const existingIndividualParticipant: ParticipantInterface = payload?.[0]?.existingIndividualParticipants?.find(
        (p: ParticipantInterface) => p?.participantId === event.target.value,
      );
      setSelectedParticipantPayload({ ...selectedParticipantPayload, existingIndividualParticipant });
    },
    [payload, selectedParticipantPayload],
  );

  const paperProps = useMemo(() => ({ style: { padding: 30, minWidth: 760 } }), []);
  const titleText = useMemo(() => {
    if (isModifyDoublesPair) return t('replacePlayer');
    if (currentParticipant && !side?.bye) return t('replaceParticipant');
    return t('assignDrawPosition');
  }, [isModifyDoublesPair, currentParticipant, side, t]);

  return (
    <TMXDialog
      {...props}
      PaperProps={paperProps}
      TransitionProps={{
        onExited,
      }}
    >
      <DialogTitle>
        <Grid container justifyContent="space-between">
          <Grid item xs={12}>
            <H3>{titleText}</H3>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container gap={2}>
          {isModifyDoublesPair && (
            <>
              <Grid item xs={12}>
                <FormLabelTextLarge>{t('selectAPlayerToReplace')}</FormLabelTextLarge>
              </Grid>
              <Grid item xs={12}>
                <RadioButtonGroup
                  handleChange={handleSelectPlayerRadioChange}
                  isRow={false}
                  options={selectDoublesPlayerRadioOptions}
                  value={
                    selectedParticipantPayload?.existingIndividualParticipant?.participantId ||
                    selectDoublesPlayerRadioOptions?.[0]?.value
                  }
                />
              </Grid>
            </>
          )}
          {currentParticipant && !isModifyDoublesPair && (
            <Grid item xs={12}>
              <FormLabelTextLarge>{isTeam ? t('currentTeam') : t('currentPlayer')}:</FormLabelTextLarge>
              <BodyLarge>{currentParticipant}</BodyLarge>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormLabelTextLarge>{isTeam ? t('chooseTeamDialogText') : t('choosePlayerDialogText')}</FormLabelTextLarge>
            <AssignParticipantsTable
              isDoubles={isDoubles}
              isModifyDoublesPair={isModifyDoublesPair}
              isTeam={isTeam}
              players={payloadParticipants}
              seedAssignments={seedAssignements}
              selectedRow={selectedParticipantPayload?.participant?.participantId}
              onChangeSelected={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container direction="row" justifyContent="flex-end" spacing={2}>
          <Grid item>
            <StandardButton onClick={handleClose}>{t('Close')}</StandardButton>
          </Grid>
          <Grid item>
            <StandardButton
              disabled={!selectedParticipantPayload?.participant}
              loading={loading || eventCostLoading}
              variant="primary"
              onClick={handleSave}
            >
              {titleText}
            </StandardButton>
          </Grid>
        </Grid>
      </DialogActions>
    </TMXDialog>
  );
};

export default AssignParticipantDialog;
